import { GetServerSideProps } from 'next';
import { ReactElement } from 'react';

import { DefaultLayout } from '@c/layouts';
import ProductsPage from '@c/ProductsPage';
import SEO from '@c/SEO';

import { CategoryDocument } from '@util/firestore/categories';
import { getProducts } from '@util/firestore/products';
import { getProductQueryArgsFromContext } from '@util/getPropsFromConext';
import { getPageByURL } from '@util/firestore/pages';
import { PaginationFilters, ListingsFilters, PageType } from '@util/hooks/useProductsPage';

import { ProductDocument } from '@util/types/firestore/products';
import { BrandDocument } from '@util/types/firestore/brand';
import { Pagination } from '@util/types/paginator';
import { ConditionDocument } from '@util/types/firestore/conditions';

import auctionHeader from '@c/headers/i/misc/Auctions-Banner-Yellow.png';
import { StaticImageData } from 'next/image';
import { toSerializable } from '@util/serializeFirestore';

// Collection configuration mapping
const COLLECTION_CONFIG: Record<string, {
  title: string;
  pageType: PageType | string;
  curatedList?: string;
  dealsFilter?: string;
  headerImage?: StaticImageData;
  headerGradient?: string;
  headerBaseBackground?: string;
  metaDescription?: string;
  defaultSort?: 'newest' | 'ranked' | 'priceAsc' | 'priceDesc';
}> = {
  'deals-and-steals': {
    title: 'Deals & Steals',
    pageType: 'deals-and-steals',
    curatedList: 'deals-and-steals',
    metaDescription: 'Shop the best deals on used camera gear and photography equipment at GearFocus.',
  },
  'featured': {
    title: 'Featured',
    pageType: 'featured',
    dealsFilter: 'Featured Listings',
    metaDescription: 'Browse featured camera gear and photography equipment from trusted sellers on GearFocus.',
  },
  'new-arrivals': {
    title: 'New Arrivals',
    pageType: 'new-arrivals',
    metaDescription: 'Discover the latest camera gear and photography equipment listings on GearFocus.',
    defaultSort: 'newest',
  },
  'auctions': {
    title: 'Auctions',
    pageType: 'auctions',
    dealsFilter: 'Auctions',
    headerImage: auctionHeader,
    headerGradient: 'bg-linear-to-r from-yellow-300 via-yellow-400 !text-black',
    headerBaseBackground: 'bg-yellow-300',
    metaDescription: 'Bid on used camera gear, lenses, and photography equipment at GearFocus auctions. Find great deals on professional photo and video gear from trusted sellers.',
  },
  'vintage': {
    title: 'Vintage Camera Gear',
    pageType: 'vintage-camera-gear',
    curatedList: 'vintage-camera-gear',
    metaDescription: 'Shop vintage and classic camera gear, film cameras, and retro photography equipment on GearFocus.',
  },
};

interface CollectionPageProps {
  slug: string;
  config: typeof COLLECTION_CONFIG[string];
  initialProducts: ProductDocument[];
  initialPageInfo: Pagination;
  initialTopFilters: PaginationFilters;
  initialSideFilters: ListingsFilters;
  categoryOptions: CategoryDocument[];
  brandOptions: BrandDocument[];
  conditionOptions: ConditionDocument[];
  pageTitle?: string;
  pageContent?: string;
}

const CollectionPage = ({
  slug,
  config,
  initialProducts,
  initialPageInfo,
  initialTopFilters,
  initialSideFilters,
  categoryOptions,
  brandOptions,
  conditionOptions,
  pageTitle,
  pageContent,
}: CollectionPageProps) => {
  const displayTitle = pageTitle || config.title;

  return (
    <>
      <SEO
        title={`${displayTitle} | GearFocus`}
        description={config.metaDescription || `Browse ${displayTitle.toLowerCase()} on GearFocus.`}
        canonicalUrl={`https://gearfocus.com/collections/${slug}`}
        ogImage="https://gearfocus.com/gear_pile.png"
      />
      <ProductsPage
        initialProducts={initialProducts}
        initialPageInfo={initialPageInfo}
        initialTopFilters={initialTopFilters}
        initialSideFilters={initialSideFilters}
        headerBreadcrumb={[
          { label: 'Home', href: '/' },
          { label: displayTitle, href: `/collections/${slug}` }
        ]}
        pageHeaderTitle={displayTitle}
        pageHeaderImage={config.headerImage}
        pageHeaderGradient={config.headerGradient}
        pageHeaderBaseBackground={config.headerBaseBackground}
        pageType={config.pageType}
        content={pageContent}
        keyword={null}
        seller_id={null}
        categoryOptions={categoryOptions}
        slugToTitle={undefined}
        brandOptions={brandOptions}
        conditionOptions={conditionOptions}
        showMarketBadges={slug === 'deals-and-steals'}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps<CollectionPageProps> = async (
  context
) => {
  const { slug } = context.params as { slug: string };

  // Check if this is a valid collection
  const config = COLLECTION_CONFIG[slug];
  if (!config) {
    return {
      notFound: true,
    };
  }

  context.res.setHeader(
    'Cache-Control',
    'public, max-age=0, s-maxage=60, stale-while-revalidate=300'
  );

  const args = await getProductQueryArgsFromContext(context);

  // Apply collection-specific default sort if no explicit sort in URL
  if (config.defaultSort && !context.query.sort) {
    args.sort = config.defaultSort;
  }

  // Apply collection-specific filters
  if (config.curatedList) {
    args.curated_list = config.curatedList;
  }
  if (config.dealsFilter) {
    args.deals?.push(config.dealsFilter);
  }

  const response = await getProducts(args);

  // Featured: randomize order so returning visitors see different items each page load.
  // SSR cache is 60s, so order changes every minute — keeps the page feeling fresh.
  if (slug === 'featured' && !context.query.sort) {
    for (let i = response.results.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [response.results[i], response.results[j]] = [response.results[j], response.results[i]];
    }
  }

  // Deals & Steals: sort by market discount and flag for market badge display.
  // marketDiscountPercent is stored on product docs and syncs to Algolia,
  // so it survives client-side pagination.
  if (slug === 'deals-and-steals') {
    // Sort by market discount (best deals first), fall back to og_price
    if (!context.query.sort) {
      response.results.sort((a: any, b: any) => {
        const marketA = a.marketDiscountPercent || 0;
        const marketB = b.marketDiscountPercent || 0;
        if (marketA || marketB) return marketB - marketA;
        const ogA = a.og_price && a.og_price > a.price ? (a.og_price - a.price) / a.og_price : 0;
        const ogB = b.og_price && b.og_price > b.price ? (b.og_price - b.price) / b.og_price : 0;
        return ogB - ogA;
      });
    }
  }

  // Try to get page content from Firestore (for pages that have custom content)
  const page = await getPageByURL(`/collections/${slug}`).catch(() => null);

  // Trim brandOptions to filter-only fields (2 of 7) — saves ~200 KB per page
  const BRAND_KEYS = new Set(['id', 'name']);
  const trimBrand = (b: Record<string, unknown>) => {
    const t: Record<string, unknown> = {};
    for (const k of BRAND_KEYS) if (k in b) t[k] = b[k];
    return t;
  };

  // Trim categoryOptions to filter-only fields (5 of 14) — saves ~110 KB per page
  const CAT_KEYS = new Set(['id', 'name', 'level', 'parents']);
  const trimCat = (c: Record<string, unknown>) => {
    const t: Record<string, unknown> = {};
    for (const k of CAT_KEYS) if (k in c) t[k] = c[k];
    return t;
  };

  // Trim listings to display-only fields — saves ~40-50 KB per page
  const LISTING_KEYS = new Set([
    'id', 'title', 'slug', 'thumbnail', 'images', 'price', 'og_price',
    'on_sale', 'out_of_stock', 'stock', 'condition', 'brand', 'categories',
    'sku', 'bumps', 'favorite_count', 'is_auction', 'start_time', 'end_time',
    'is_verified', 'is_flat_rate', 'seller_id', 'shipping_costs', 'gearMaster',
    'external_source', 'integrations', 'description', 'account_id',
    'date_added', 'rank_score',
    'marketDiscountPercent', 'marketSavings', 'showMarketBadge',
  ]);
  const trimListing = (l: Record<string, unknown>) => {
    const t: Record<string, unknown> = {};
    for (const k of LISTING_KEYS) if (k in l) t[k] = l[k];
    return t;
  };

  return {
    // `pageTitle` is undefined when a collection has no CMS page doc, which
    // Next rejects; toSerializable omits undefined keys, so the component's
    // `pageTitle || config.title` fallback works as intended.
    props: toSerializable({
      slug,
      config,
      initialProducts: response.results.map(r => trimListing(r as Record<string, unknown>)) as typeof response.results,
      initialPageInfo: response.pageInfo,
      categoryOptions: args.categoryOptions.map(c => trimCat(c as Record<string, unknown>)) as typeof args.categoryOptions,
      conditionOptions: args.conditionOptions,
      brandOptions: args.brandOptions.map(b => trimBrand(b as Record<string, unknown>)) as typeof args.brandOptions,
      initialTopFilters: {
        sort: args.sort,
        limit: args.limit,
        view: (context.query.view as 'list') || 'grid',
      },
      initialSideFilters: {
        minPrice: args.minPrice,
        maxPrice: args.maxPrice,
        condition: args.conditionFilters,
        brand: args.brandFilters,
        category: args.categoryFilters,
        deals: args.deals,
        useCases: args.useCases,
        additional: args.additionalFilters,
      },
      pageTitle: page?.title ?? undefined,
      pageContent: page?.content ?? '',
    }),
  };
};

CollectionPage.getLayout = (page: ReactElement) => {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export default CollectionPage;
